import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import api from '../../../utils/api';

const SearchBar = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const searchRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    const fetchProducts = async () => {
      try {
        const { data } = await api.get('/products');
        if (!Array.isArray(data)) throw new Error('Product response has an unexpected shape');
        if (active) {
          setProducts(data);
          setError('');
        }
      } catch (requestError) {
        console.error('Failed to fetch products for dashboard search:', requestError);
        if (active) setError('Products could not be loaded from the backend.');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchProducts();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchQuery?.trim()) {
      const filtered = products.filter(product =>
        product?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.sku?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        (Array.isArray(product?.category) ? product.category : [product?.category])
          .some(category => category?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
      ).slice(0, 6);
      setSuggestions(filtered);
      setIsSearchOpen(true);
    } else {
      setSuggestions([]);
      setIsSearchOpen(false);
    }
  }, [products, searchQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e?.target?.value);
  };

  const handleSuggestionClick = (product) => {
    setSearchQuery('');
    setIsSearchOpen(false);
    navigate(`/product-details/${encodeURIComponent(product.id)}`);
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      navigate('/product-catalog', { state: { searchQuery } });
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  };

  const getStockStatus = (stock) => {
    if (stock === null || stock === undefined || stock === '' || !Number.isFinite(Number(stock))) {
      return { color: 'text-muted-foreground', label: 'N/A' };
    }
    if (Number(stock) <= 5) return { color: 'text-error', label: 'Low' };
    if (Number(stock) <= 20) return { color: 'text-warning', label: 'Medium' };
    return { color: 'text-success', label: 'Good' };
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-md">
      <form onSubmit={handleSearchSubmit}>
        <div className="relative">
          <Input
            type="search"
            placeholder="Search products..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="pl-10 pr-4 h-9 sm:h-10 text-sm"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <Icon name="Search" size={14} className="text-muted-foreground sm:hidden" />
            <Icon name="Search" size={16} className="text-muted-foreground hidden sm:block" />
          </div>
        </div>
      </form>
      {isSearchOpen && loading && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-modal z-50 p-4 text-sm text-muted-foreground">
          Loading products...
        </div>
      )}
      {isSearchOpen && !loading && error && (
        <div role="alert" className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-modal z-50 p-4 text-sm text-destructive">
          {error}
        </div>
      )}
      {isSearchOpen && !loading && !error && suggestions?.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-modal z-50 max-h-60 sm:max-h-80 overflow-y-auto">
          <div className="p-1 sm:p-2">
            <div className="text-xs text-muted-foreground px-2 sm:px-3 py-1 sm:py-2 border-b border-border">
              Results ({suggestions?.length})
            </div>
            
            {suggestions?.map((product) => {
              const stockStatus = getStockStatus(product?.quantity);
              return (
                <button
                  key={product?.id}
                  onClick={() => handleSuggestionClick(product)}
                  className="w-full p-3 hover:bg-muted/50 rounded-lg transition-hover text-left group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <Icon name="Package" size={16} className="text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground group-hover:text-primary transition-hover truncate">
                            {product?.name}
                          </p>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs font-data text-muted-foreground">
                              {product?.sku}
                            </span>
                            <span className="text-xs text-muted-foreground">•</span>
                            <span className="text-xs text-muted-foreground">
                              {product?.category}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 flex-shrink-0 ml-3">
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Stock</p>
                        <p className={`text-sm font-medium ${stockStatus?.color}`}>
                          {product?.quantity ?? 'N/A'}
                        </p>
                      </div>
                      <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          
          <div className="border-t border-border p-3">
            <button
              onClick={handleSearchSubmit}
              className="w-full text-sm text-primary hover:text-primary/80 font-medium transition-hover text-center"
            >
              View all results for "{searchQuery}"
            </button>
          </div>
        </div>
      )}
      {isSearchOpen && !loading && !error && suggestions?.length === 0 && searchQuery?.trim() && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-modal z-50">
          <div className="p-6 text-center">
            <Icon name="Search" size={24} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              No products found for "{searchQuery}"
            </p>
            <button
              onClick={() => navigate('/product-catalog')}
              className="text-sm text-primary hover:text-primary/80 font-medium mt-2 transition-hover"
            >
              Browse all products
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;